import React, { useRef } from "react";
import moment, { Moment } from "moment";
import { gql } from "__generated__/gql";
import { intl } from "LocaleWrapper";

import { useApolloClient, useMutation } from "@apollo/client";
import { Table } from "@cofano/cck";
import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";

import { TableId, useBackendTable } from "components/table";

import { usePermission } from "imports/Utils";

import { OPEN_DECLARATION_MUTATION, OpenDeclarationSwitch } from "./PurchaseCharters";
import { FINANCIALLY_UNREADY_MUTATION } from "./Invoicelines";

import DeclarationProgress from "./components/progress/DeclarationProgress";

import { Charter } from "types/index";

import { CharterSubtype, CharterType } from "__generated__/enums";
import { ChildPropsFromPagination } from "types/commons";
import { Charters_SelectableSalesChartersQuery } from "__generated__/graphql";

type SalesCharterFromQuery = ChildPropsFromPagination<Charters_SelectableSalesChartersQuery, "charters">
type SalesCharterOnly = Extract<SalesCharterFromQuery, { __typename?: "SalesCharter" }>;

const SelectableSalesCharters = ({
	selectedCharterId,
	onSelect,
	onChange,
	window,
	actuallyUpdateSaleCharterRequestId,
	updateSaleCharterRequestId,
	setLoading,
}: {
	selectedCharterId?: number;
	onSelect: (id: number) => void;
	onChange: (charters: Charter[]) => void;
	window: {
		from: Moment;
		to: Moment;
	};
	actuallyUpdateSaleCharterRequestId: () => void;
	updateSaleCharterRequestId: number;
	setLoading: (loadingState: boolean) => void;
}) => {

	const client = useApolloClient();
	const { isAdmin } = usePermission();
	const canOpen = isAdmin;
	const windowRef = useRef(window);

	const { tableProps, refetch } = useBackendTable({
		id: TableId.DeclarationSelectableSalesCharters,
		onChange: async(pagination, sort, filter, callback) => {
			setLoading(true);

			try {
				const result = await client.query({
					query: query,
					variables: {
						search: {
							isActive: true,
							hasContract: true,
							hasActiveContract: true,
							validFrom: moment(windowRef.current.from).unix() * 1000,
							validUntil: moment(windowRef.current.to).unix() * 1000,
							charterType: CharterType.Sales,
							charterSubtypes: [ CharterSubtype.Spot, CharterSubtype.Contract, CharterSubtype.Time ],
						},
						pagination: {
							...pagination,
							sortList: sort,
						},
						from: moment(windowRef.current.from).unix() * 1000,
						to: moment(windowRef.current.to).unix() * 1000,
					},
				});

				callback({
					content: result.data.charters.content ?? [],
					totalResults: result.data.charters.totalElements,
				});

				onChange(result.data.charters.content as Charter[]);
				setLoading(false);
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});

				setLoading(false);
			}
		},
	});

	const [ financiallyUnreadyDeclaration ] = useMutation(FINANCIALLY_UNREADY_MUTATION, {
		onCompleted: () => {
			actuallyUpdateSaleCharterRequestId();

			if (selectedCharterId) {
				onSelect(selectedCharterId);
			}
		},
	});

	const [ openDeclaration ] = useMutation(OPEN_DECLARATION_MUTATION, {
		onCompleted: () => {
			actuallyUpdateSaleCharterRequestId();

			if (selectedCharterId) {
				onSelect(selectedCharterId);
			}
		},
	});

	usePropertyChangeEffect(() => {
		refetch();
	}, [ updateSaleCharterRequestId ]);

	usePropertyChangeEffect(() => {
		windowRef.current = window;
		refetch(0);
	}, [ window.from, window.to ]);

	return (
		<React.Fragment>
			<Table
				{...tableProps}
				onRowClick={(row) => {
					if (row.id) {
						onSelect(row.id);
					}
				}}
				highlightRow={row => {
					if (selectedCharterId && (row.id === selectedCharterId)) {
						return "HIGHLIGHT";
					} else {
						return "NONE";
					}
				}}
				columns={[ {
					id: "charterNumber",
					title: intl.formatMessage({ id: "CHARTER" }),
					width: 150,
				}, {
					id: "financiallyReadiedOn",
					width: 100,
					title: intl.formatMessage({ id: "FINANCIALLY_READY" }),
					render: (row) => {
						if (row.declarations?.[0]?.financiallyReadiedOn) {
							return (
								<OpenDeclarationSwitch
									canOpen={canOpen && !row.declarations[0].closedOn}
									declaration={row.declarations[0]}
									financial
									callback={financiallyUnreadyDeclaration}
								/>
							);
						} else {
							return intl.formatMessage({ id: "NO" });
						}
					},
				}, {
					id: "filled",
					title: intl.formatMessage({ id: "PURCHASE_CHARTER_FILLED" }),
					width: 60,
					render: (row: SalesCharterOnly) => {
						if (row.declarations?.[0]) {
							if (row.declarations[0].closedOn) {
								return (
									<OpenDeclarationSwitch
										canOpen={canOpen}
										declaration={row.declarations[0]}
										financial={false}
										callback={openDeclaration} />
								);
							}

							const checks = row.declarations[0].progress?.progressChecks || [];

							return (
								<DeclarationProgress
									checkedCount={checks.filter(c => c?.checked).length}
									totalCount={checks.length}
									value={row.declarations[0].progress?.percentage ?? 0}
								/>
							);
						} else {
							return (
								<DeclarationProgress
									checkedCount={0}
									totalCount={0}
									value={0}
								/>
							);
						}
					},
				} ]} />
		</React.Fragment>
	);
};

const query = gql(`#graphql
	query charters_selectableSalesCharters($search: CharterSearch!, $pagination: Pagination, $from: Instant, $to: Instant) {
		charters(search: $search, pagination: $pagination) {
			totalElements
			content {
				id
				type
				charterNumber
				... on SalesCharter {
					declarations(from: $from, to: $to) {
						id
						declarationValues {
							id
						}
						closedOn
						financiallyReadiedOn
						invoiceLines {
							id
							invoiceReference
							description
						}
						progress {
							percentage
							progressChecks {
								description
								checked
							}
						}
					}
				}
			}
		}
	}
`);

export default SelectableSalesCharters;
